import React from 'react';
import clsx from 'clsx';
import { Buffer } from 'buffer';
import { type Dispatch } from 'redux';
import { useDispatch } from 'react-redux';

import { alpha, Box, Button, Card, CardActions, CardContent, IconButton, Typography, useTheme } from '@mui/material';

import { Icon, Text, useSettings, Utils } from 'app/main/components';

type NotificationVariant = 'info' | 'success' | 'warning' | 'error';

interface NotificationActionProps {
	label: string;
	icon: string;
	url: string;
	type: 'link' | 'carbone-download';
	dismiss: boolean;
}

interface NotificationCardProps {
	className?: string;
	notification: {
		id: string | number;
		createdAt?: string | null;
		isRead: boolean;
		title: string;
		body: string;
		url: string;
		icon: string;
		variant: NotificationVariant;
		actions: NotificationActionProps[];
	};
	onLinkClick?: (id: string | number) => void;
	onDismiss: (id: string | number) => void;
}

type IconVariantsType = {
	[key in NotificationVariant]: string;
};

const iconVariants: IconVariantsType = {
	info: 'info',
	success: 'check-circle',
	warning: 'error',
	error: 'cancel'
};

function downloadCarboneDocument(dispatch: Dispatch<any>, url: string, onSuccess?: VoidFunction) {
	Utils.executeApi
		.get({
			url,
			dispatch,
			successHandler: response => {
				const { filename, mimeType, content } = response.data;

				const decoded = Buffer.from(content, 'base64');
				const contentBlob = new Blob([decoded.toString()], { type: mimeType });
				const href = window.URL.createObjectURL(contentBlob);

				const link = document.createElement('a');
				link.href = href;
				link.setAttribute('download', filename);
				document.body.appendChild(link);
				link.click();

				document.body.removeChild(link);
				URL.revokeObjectURL(href);
			}
		})
		.then(() => onSuccess?.());
}

function CardActionButton({ action, handleClose }: { action: NotificationActionProps; handleClose: () => void }) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const handleClick = () => {
		if (action.type === 'carbone-download') {
			downloadCarboneDocument(dispatch, action.url, action.dismiss ? handleClose : undefined);
		} else {
			Utils.openLink({ pathname: action.url, newTab: true });
		}
	};

	return (
		<Button
			size="small"
			startIcon={<Icon className="text-12 opacity-75" icon={action.icon} />}
			onClick={handleClick}
		>
			{t(action.label)}
		</Button>
	);
}

function getIcon(variant: NotificationVariant, icon: string) {
	if (icon) return icon;
	return iconVariants[variant] || '';
}

export default function NotificationCard({ className, notification, onLinkClick, onDismiss }: NotificationCardProps) {
	const theme = useTheme();
	const { id, createdAt, isRead, title, body, url, icon, variant, actions } = notification;

	const handleClose = () => {
		if (onDismiss) onDismiss(id);
	};

	const handleTitleClick = () => {
		if (onLinkClick) onLinkClick(id);
		Utils.openLink({ pathname: url, newTab: true });
	};

	const variantMainColor = variant ? theme.palette[variant].main : null;

	return (
		<Card
			className={clsx('relative flex h-full min-h-64 w-full items-stretch rounded-16', className)}
			variant="outlined"
			sx={{ backgroundColor: isRead ? theme.palette.background.default : 'inherit' }}
		>
			{icon && (
				<Box
					className="p-8"
					sx={{
						backgroundColor: !isRead
							? variantMainColor
							: variantMainColor
								? alpha(variantMainColor, 0.3)
								: undefined,
						color: variantMainColor
							? theme.palette.getContrastText(!isRead ? variantMainColor : alpha(variantMainColor, 0.3))
							: 'inherit'
					}}
				>
					<Icon className="m-2 opacity-80" icon={getIcon(variant, icon)} size={26} />
				</Box>
			)}
			<CardContent className="w-full p-8 pr-40 last:pb-8">
				<Text
					className={clsx(!isRead ? 'font-bold' : 'font-semibold')}
					color={!isRead ? 'text.primary' : 'text.secondary'}
					onClick={url ? handleTitleClick : undefined}
				>
					{title}
					{!!url && <Icon className="align-middle m-4 inline" icon="open-in-new" size={14} />}
				</Text>
				<Typography>{body}</Typography>
				<Typography variant="caption">{Utils.parseDate(createdAt)?.fromNow()}</Typography>
				{actions && (
					<CardActions className="p-4">
						{actions.map((action, index) => (
							<CardActionButton key={index} action={action} handleClose={handleClose} />
						))}
					</CardActions>
				)}
			</CardContent>
			<IconButton
				className="absolute right-0 top-0 m-4"
				color="inherit"
				size="small"
				onClick={handleClose}
				disableRipple
			>
				<Icon icon="close" className="text-12 opacity-75" />
			</IconButton>
		</Card>
	);
}
